import { apiService } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';
import { extractApiData, isApiSuccess } from '../../utils/helpers';

export interface PermissionUser {
  userId: number;
  userType: string; // "ADMIN" | "MERCHANT"
  name: string;
  email: string;
  role: string;
  /** MERCHANT users only — the merchant they belong to (for the merchant-first picker). */
  merchantId?: number;
  merchantName?: string;
  /** Super admins can't have their permissions edited. */
  isSuperAdmin: boolean;
}

export interface PermissionModuleRow {
  moduleId: number;
  moduleCode: string;
  moduleName: string;
  moduleIcon?: string;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canExport: boolean;
  overridden: boolean;
}

export interface UserPermissionMatrix {
  userId: number;
  userType: string;
  roleId?: number;
  roleName?: string;
  modules: PermissionModuleRow[];
}

/** Flattens the platform-user list response (admins + merchants) into a simple pick list. */
export async function fetchPermissionUsers(): Promise<PermissionUser[]> {
  const response: unknown = await apiService.post(API_PATHS.GET_PLATFORM_USERS, {
    page: 0,
    size: 1000,
  });
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to load users.');
  }
  const data = extractApiData<{
    content?: any[];
    admins?: any[];
    merchants?: any[];
  }>(response) ?? {};

  const rows = [
    ...(data.content ?? []),
    ...(data.admins ?? []),
    ...(data.merchants ?? []),
  ];

  // De-dupe (content may already include both lists) by userType+userId.
  const seen = new Set<string>();
  const users: PermissionUser[] = [];
  for (const r of rows) {
    const userId = Number(r?.userId ?? 0);
    const userType = String(r?.userType ?? '').toUpperCase();
    if (!userId || !userType) continue;
    const key = `${userType}:${userId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const role = String(r?.role ?? '—');
    const merchantId = r?.merchantId != null ? Number(r.merchantId) : undefined;
    users.push({
      userId,
      userType,
      name: String(r?.name ?? '—'),
      email: String(r?.email ?? '—'),
      role,
      merchantId: merchantId && merchantId > 0 ? merchantId : undefined,
      merchantName: userType === 'MERCHANT' ? String(r?.companyName ?? '—') : undefined,
      isSuperAdmin: userType === 'ADMIN' && /super/i.test(role),
    });
  }
  return users;
}

export async function fetchUserPermissions(
  userId: number,
  userType: string,
): Promise<UserPermissionMatrix> {
  const response: unknown = await apiService.post(API_PATHS.GET_USER_PERMISSIONS, {
    userId,
    userType,
  });
  if (!isApiSuccess(response)) {
    throw new Error(
      (response as { message?: string })?.message || 'Unable to load permissions.',
    );
  }
  const data = extractApiData<UserPermissionMatrix>(response);
  if (!data) throw new Error('Unable to load permissions.');
  return { ...data, modules: data.modules ?? [] };
}

export async function saveUserPermissions(payload: {
  userId: number;
  userType: string;
  modules: Array<{
    moduleId: number;
    canView: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    canExport: boolean;
  }>;
}): Promise<void> {
  const response: unknown = await apiService.post(API_PATHS.SAVE_USER_PERMISSIONS, payload);
  if (!isApiSuccess(response)) {
    throw new Error(
      (response as { message?: string })?.message || 'Unable to save permissions.',
    );
  }
}
